/**
 * MeetingAttendees — the shared attendee pill list for an `ahoy_meeting`.
 *
 * Extracted from MeetingSummary so the same attendee resolution (invitations
 * matched against the meeting's associated contacts/users, upgraded via the CRM
 * object store) can be reused wherever a meeting's attendees are shown — e.g.
 * inside an expanded meeting-notes card. Renders the shared ParticipantPillList
 * (which renders nothing when there are no attendees).
 */

import { useMemo } from 'react';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import type { Participant } from '@/lib/crm/components/ParticipantPillList';
import { ParticipantPillList } from '@/lib/crm/components/ParticipantPillList';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { useStoreResolvedParticipants } from '@/lib/crm/hooks/useStoreResolvedParticipants';
import { useCrmObject } from '@/lib/crm/store';
import {
  getEntityEmails,
  preferResolvedEmailEntity,
} from '@/lib/crm/store/crmObjectStore';
import { mapInvitationsToGuests } from '@/pages/calendar/utils/eventMapper';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { getGuestDisplayName } from '../utils/meetingHelpers';

export function MeetingAttendees({ meeting }: { meeting: CrmObject }) {
  // Scope association/member lookups to the meeting's own org so attendee
  // matching stays consistent with the fetched meeting object — the current
  // org context may be unset or a different org in slideout/expanded-card
  // contexts (see the participant resolution below, which uses meeting.org_id).
  const { associations: resolvedAssociations } = useAssociations(
    meeting.org_id,
    meeting.associations,
    { includeAssociations: false }
  );

  const { members } = useOrgMembers(meeting.org_id);

  // Parse invitations to guests.
  const guests = useMemo(() => {
    const invitations = getPropertyValue<
      Array<{
        email?: string;
        invitee_name?: string;
        response_status?: string;
        is_organizer?: string;
        is_optional?: string;
      }>
    >(meeting.properties?.ahoy_invitations);
    return mapInvitationsToGuests(invitations);
  }, [meeting.properties?.ahoy_invitations]);

  // Build email→CrmObject map for attendee→contact matching. Associations from
  // both ahoy_meeting_to_contact and ahoy_meeting_to_user are considered; the
  // second loop below supplements user associations using OrgMember email lookup.
  const contactsByEmail = useMemo(() => {
    const map = new Map<string, CrmObject>();

    for (const assoc of resolvedAssociations) {
      if (
        (assoc.association_type_name === 'ahoy_meeting_to_contact' ||
          assoc.association_type_name === 'ahoy_meeting_to_user') &&
        assoc.object
      ) {
        for (const email of getEntityEmails(assoc.object)) {
          map.set(
            email,
            preferResolvedEmailEntity(map.get(email), assoc.object)
          );
        }
      }
    }

    for (const assoc of resolvedAssociations) {
      if (
        assoc.association_type_name === 'ahoy_meeting_to_user' &&
        assoc.object
      ) {
        const assocEntityId = assoc.object?.entity_id;
        const member = members.find(m => m.entityId === assocEntityId);
        if (member?.email) {
          const key = member.email.toLowerCase();
          map.set(key, preferResolvedEmailEntity(map.get(key), assoc.object));
        }
      }
    }

    return map;
  }, [resolvedAssociations, members]);

  const participants = useMemo<Participant[]>(() => {
    return guests
      .filter(g => typeof g.email === 'string')
      .map((guest, index) => ({
        key: `${guest.email}:${index}`,
        object: contactsByEmail.get(guest.email.toLowerCase()),
        email: guest.email,
        name: guest.name || getGuestDisplayName(guest),
        rsvpStatus: guest.response_status,
      }));
  }, [guests, contactsByEmail]);

  const resolvedParticipants = useStoreResolvedParticipants({
    participants,
    orgId: meeting.org_id,
  });

  return (
    <ParticipantPillList
      label='Attendees'
      participants={resolvedParticipants}
      orgId={meeting.org_id}
    />
  );
}

/**
 * Fetches the full meeting (invitations + associations) by id and renders its
 * attendees. Use where only the meeting id is known and the fetch should happen
 * on demand — e.g. an expanded meeting-notes card mounts this only once opened.
 */
export function MeetingAttendeesById({
  meetingId,
  orgId,
}: {
  meetingId: string;
  orgId: number;
}) {
  const { object: meeting } = useCrmObject(orgId, 'ahoy_meeting', meetingId);
  if (!meeting) {
    return null;
  }
  return <MeetingAttendees meeting={meeting} />;
}
